import { useEffect, useRef, useState } from "react";

const songUrl = process.env.PUBLIC_URL + "/song.mp3";
const targetVolume = 0.55;
const fadeInMilliseconds = 2400;
const unlockEvents = ["pointerdown", "touchstart", "keydown"];

const fadeIn = (audio) => {
  const startedAt = performance.now();
  audio.volume = 0;
  const step = (now) => {
    const progress = Math.min(1, (now - startedAt) / fadeInMilliseconds);
    audio.volume = targetVolume * progress;
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

export default function BackgroundSong() {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const removeUnlock = () => unlockEvents.forEach((name) => window.removeEventListener(name, unlock, true));
    const start = () =>
      audio.play().then(
        () => {
          fadeIn(audio);
          removeUnlock();
          return true;
        },
        () => false
      );
    function unlock() {
      if (audio.paused) start();
      else removeUnlock();
    }

    start().then((started) => {
      if (!started) unlockEvents.forEach((name) => window.addEventListener(name, unlock, true));
    });

    const syncPlaying = () => setIsPlaying(!audio.paused);
    audio.addEventListener("play", syncPlaying);
    audio.addEventListener("pause", syncPlaying);
    return () => {
      removeUnlock();
      audio.removeEventListener("play", syncPlaying);
      audio.removeEventListener("pause", syncPlaying);
    };
  }, []);

  const toggleMute = (event) => {
    event.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.muted = false;
      setIsMuted(false);
      audio.play().then(() => fadeIn(audio), () => {});
      return;
    }
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  };

  const isSilent = !isPlaying || isMuted;

  return (
    <>
      <audio ref={audioRef} src={songUrl} loop preload="auto" playsInline />
      <button
        type="button"
        className="song-toggle"
        aria-label={isSilent ? "play music" : "mute music"}
        onClick={toggleMute}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d="M9 18V6l10-2v12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="6.5" cy="18" r="2.5" fill="currentColor" />
          <circle cx="16.5" cy="16" r="2.5" fill="currentColor" />
          {isSilent && <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />}
        </svg>
      </button>
    </>
  );
}
